import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import findResult, { hasMinimumLength } from '@/utils/resultsHelper';
import { clientError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import RibbonMenu from '@/containers/ribbon/RibbonMenu';
import {
  updateRole,
  updateRoleProperties,
  editRoleCancel,
  showConfirmPermissions,
} from '../actions';
import { permissionsDiff, roleDiff } from '../helper';

const SaveRoleAction = ({
  role,
  isEditing,
  isDirty,
  exitEdit,
  updateStatus,
  code,
  category,
  save,
  updateProperties,
  showConfirm,
}) => {
  const intl = useIntl();
  const isSaving = (updateStatus === states.PENDING);

  const setErrors = (newErrors) => (updateProperties(code, category, { updateErrors: newErrors }));

  const validateRole = () => {
    const validationErrors = [];

    if (!hasMinimumLength(role.name)) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.name.title' }),
          intl.formatMessage({ id: 'validation.name.required' }),
          [fields.NAME],
        ),
      );
    }

    if (role.name && role.name.length > globalFieldLengths.name) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.name.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.name }),
          [fields.NAME],
        ),
      );
    }

    if (role.description && role.description.length > globalFieldLengths.description) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.description.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.description }),
          [fields.DESCRIPTION],
        ),
      );
    }

    if (role.permissions.length === 0) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.permissions.title' }),
          intl.formatMessage({ id: 'save.role.validation.permissions.required' }),
          [fields.PERMISSIONS],
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateRole();
    setErrors(validationErrors);

    return validationErrors;
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();
    if (validationErrors.length === 0) {
      const permissions = permissionsDiff(role);
      if (permissions.length > 0) {
        showConfirm(code, category);
      } else if (roleDiff(role)) {
        save(category, code);
      } else {
        exitEdit(category, code);
      }
    }
  };

  return (
    <RibbonMenu
      name="save"
      icon="save"
      disabled={!isEditing || !isDirty}
      message="actions.save"
      onClick={submitForm}
      className="aridhia-save-role-action"
      loading={isSaving}
    />
  );
};

const mapStateToProps = ({
  roles: { roles },
}, { category, code }) => {
  const role = findResult(roles, category, code);
  return {
    updateStatus: role ? role.updateStatus : states.NONE,
    role,
    isDirty: role ? role.isDirty : false,
  };
};

const mapDispatchToProps = {
  save: updateRole,
  updateProperties: updateRoleProperties,
  exitEdit: editRoleCancel,
  showConfirm: showConfirmPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveRoleAction);
